import { ReactComponent as BlockArrowIcon } from 'assets/icons/shapes/block_arrow.svg';
import { ReactComponent as BlockArrowIndentIcon } from 'assets/icons/shapes/block_arrow_indent.svg';
import { ReactComponent as CircleIcon } from 'assets/icons/shapes/circle.svg';
import { ReactComponent as DiamondIcon } from 'assets/icons/shapes/diamond.svg';
import { ReactComponent as DoubleBlockArrowIcon } from 'assets/icons/shapes/double_block_arrow.svg';
import { ReactComponent as HexagonIcon } from 'assets/icons/shapes/hexagon.svg';
import { ReactComponent as LeafLeftIcon } from 'assets/icons/shapes/leaf_left.svg';
import { ReactComponent as OctagonIcon } from 'assets/icons/shapes/octagon.svg';
import { ReactComponent as OvalIcon } from 'assets/icons/shapes/oval.svg';
import { ReactComponent as ParallelogramIcon } from 'assets/icons/shapes/parallelogram.svg';
import { ReactComponent as PentagonIcon } from 'assets/icons/shapes/pentagon.svg';
import { ReactComponent as PillIcon } from 'assets/icons/shapes/pill.svg';
import { ReactComponent as QuarterCircleIcon } from 'assets/icons/shapes/quarter_circle.svg';
import { ReactComponent as RectangleIcon } from 'assets/icons/shapes/rectangle.svg';
import { ReactComponent as RibbonIcon } from 'assets/icons/shapes/ribbon.svg';
import { ReactComponent as RightTriangleIcon } from 'assets/icons/shapes/right_triangle.svg';
import { ReactComponent as SemiCircleIcon } from 'assets/icons/shapes/semi_circle.svg';
import { ReactComponent as SquareIcon } from 'assets/icons/shapes/square.svg';
import { ReactComponent as TrapezoidIcon } from 'assets/icons/shapes/trapezoid.svg';
import { ReactComponent as TriangleIcon } from 'assets/icons/shapes/triangle.svg';
import { ReactElement } from 'react';
import { BasicShapeType, BasicShapeWidgetData } from 'widgets/BasicShapeWidget/BasicShapeWidget.types';

export const DEFAULT_WIDTH = 100;
export const DEFAULT_HEIGHT = 100;

export const DEFAULT_FILL_COLOR = '#2b2b35';
export const DEFAULT_BORDER_WIDTH = 5;
export const DEFAULT_SECONDARY_FILL_COLOR = '#F64D2A';

export const FLEXIBLE_RATIO_SHAPES = [
  'semi-circle',
  'parallelogram',
  'trapezoid',
  'block-arrow',
  'block-arrow-indent',
  'ribbon',
  'double-block-arrow',
];

interface Dimension {
  widthPx: number;
  heightPx: number;
}

export interface BasicShapeWidgetItem {
  key:
    | 'vg-icon-square'
    | 'vg-icon-rounded-square'
    | 'vg-icon-rectangle'
    | 'vg-icon-rounded-rectangle'
    | 'vg-icon-circle'
    | 'vg-icon-oval'
    | 'vg-icon-half-circle-right'
    | 'vg-icon-pill'
    | 'vg-icon-triangle-right'
    | 'vg-icon-triangle'
    | 'vg-icon-leaf-left'
    | 'vg-icon-leaf-right'
    | 'vg-icon-diamond-shape'
    | 'vg-icon-pentagon'
    | 'vg-icon-hexagon'
    | 'vg-icon-octagon'
    | 'vg-icon-quarter-circle'
    | 'vg-icon-parallelogram'
    | 'vg-icon-trapezoid'
    | 'vg-icon-block-arrow'
    | 'vg-icon-block-arrow-indent'
    | 'vg-icon-ribbon'
    | 'vg-icon-double-block-arrow';
  type: BasicShapeType;
  label: string;
  iconDimensions: Dimension;
  options?: Partial<BasicShapeWidgetData>;
  icon?: ReactElement;
}

const PILL_CORNER_RADIUS = 50;
const ROUNDED_RECT_CORNER_RADIUS = 10;

const DEFAULT_ICON_WIDTH = 46;
const DEFAULT_ICON_HEIGHT = 46;
const DEFAULT_HEXAGON_WIDTH = (46 * 4) / (2 * Math.sqrt(3));

const ICON_ONE_TO_ONE_DIM = { widthPx: DEFAULT_ICON_WIDTH, heightPx: DEFAULT_ICON_HEIGHT };
const ICON_FLAT_LANDSCAPE_DIM = { widthPx: (DEFAULT_ICON_WIDTH * 6) / 5, heightPx: DEFAULT_ICON_HEIGHT / 2 };
const ICON_LANDSCAPE_DIM = { widthPx: DEFAULT_ICON_WIDTH, heightPx: DEFAULT_ICON_HEIGHT / 2 };
const ICON_HEXAGON_DIM = { widthPx: DEFAULT_HEXAGON_WIDTH, heightPx: DEFAULT_ICON_HEIGHT };

export const ICON_TO_WIDGET_SCALE = 4;

export const ONE_ONE_RATIO_SHAPES = [BasicShapeType.Ellipse, BasicShapeType.Octagon];

/**
 * Array of basic shape widgets that will be displayed in the left panel
 * For each shape in the array, a fill-only and border-only option will be added
 * to the menu.
 */
export const BASIC_SHAPE_WIDGET_ITEMS: Array<BasicShapeWidgetItem> = [
  {
    key: 'vg-icon-square',
    type: BasicShapeType.Rectangle,
    label: 'Square',
    iconDimensions: { ...ICON_ONE_TO_ONE_DIM },
  },
  {
    key: 'vg-icon-rounded-square',
    type: BasicShapeType.Rectangle,
    label: 'Rounded Square',
    iconDimensions: { ...ICON_ONE_TO_ONE_DIM },
    options: { cornerRadius: ROUNDED_RECT_CORNER_RADIUS },
    icon: <SquareIcon />,
  },
  {
    key: 'vg-icon-rectangle',
    type: BasicShapeType.Rectangle,
    label: 'Rectangle',
    iconDimensions: { ...ICON_LANDSCAPE_DIM },
  },
  {
    key: 'vg-icon-rounded-rectangle',
    type: BasicShapeType.Rectangle,
    label: 'Rounded Rectangle',
    iconDimensions: { ...ICON_LANDSCAPE_DIM },
    options: { cornerRadius: ROUNDED_RECT_CORNER_RADIUS },
    icon: <RectangleIcon />,
  },
  {
    key: 'vg-icon-circle',
    type: BasicShapeType.Ellipse,
    label: 'Circle',
    iconDimensions: { ...ICON_ONE_TO_ONE_DIM },
    icon: <CircleIcon />,
  },
  {
    key: 'vg-icon-oval',
    type: BasicShapeType.Ellipse,
    label: 'Oval',
    iconDimensions: { ...ICON_LANDSCAPE_DIM },
    icon: <OvalIcon />,
  },
  {
    key: 'vg-icon-half-circle-right',
    type: BasicShapeType.SemiCircle,
    label: 'Semi Circle',
    iconDimensions: { ...ICON_LANDSCAPE_DIM },
    icon: <SemiCircleIcon />,
  },
  {
    key: 'vg-icon-pill',
    type: BasicShapeType.Rectangle,
    label: 'Pill',
    iconDimensions: { ...ICON_LANDSCAPE_DIM },
    options: { cornerRadius: PILL_CORNER_RADIUS },
    icon: <PillIcon />,
  },
  {
    key: 'vg-icon-triangle-right',
    type: BasicShapeType.RightTriangle,
    label: 'Right Triangle',
    iconDimensions: { ...ICON_ONE_TO_ONE_DIM },
    icon: <RightTriangleIcon />,
  },
  {
    key: 'vg-icon-triangle',
    type: BasicShapeType.Triangle,
    label: 'Triangle',
    iconDimensions: { ...ICON_ONE_TO_ONE_DIM },
    icon: <TriangleIcon />,
  },
  {
    key: 'vg-icon-leaf-left',
    type: BasicShapeType.LeafLeft,
    label: 'Leaf Left',
    iconDimensions: { ...ICON_ONE_TO_ONE_DIM },
    icon: <LeafLeftIcon />,
  },
  {
    key: 'vg-icon-leaf-right',
    type: BasicShapeType.LeafRight,
    label: 'Leaf Right',
    iconDimensions: { ...ICON_ONE_TO_ONE_DIM },
  },
  {
    key: 'vg-icon-diamond-shape',
    type: BasicShapeType.Diamond,
    label: 'Diamond',
    iconDimensions: { ...ICON_ONE_TO_ONE_DIM },
    icon: <DiamondIcon />,
  },
  {
    key: 'vg-icon-pentagon',
    type: BasicShapeType.Pentagon,
    label: 'Pentagon',
    iconDimensions: { ...ICON_ONE_TO_ONE_DIM },
    icon: <PentagonIcon />,
  },
  {
    key: 'vg-icon-hexagon',
    type: BasicShapeType.Hexagon,
    label: 'Hexagon',
    iconDimensions: { ...ICON_HEXAGON_DIM },
    icon: <HexagonIcon />,
  },
  {
    key: 'vg-icon-octagon',
    type: BasicShapeType.Octagon,
    label: 'Octagon',
    iconDimensions: { ...ICON_ONE_TO_ONE_DIM },
    icon: <OctagonIcon />,
  },
  {
    key: 'vg-icon-quarter-circle',
    type: BasicShapeType.QuarterCircle,
    label: 'Quarter Circle',
    iconDimensions: { ...ICON_ONE_TO_ONE_DIM },
    icon: <QuarterCircleIcon />,
  },
  {
    key: 'vg-icon-parallelogram',
    type: BasicShapeType.Parallelogram,
    label: 'Parallelogram Slant Right',
    iconDimensions: { ...ICON_LANDSCAPE_DIM },
    icon: <ParallelogramIcon />,
  },
  {
    key: 'vg-icon-trapezoid',
    type: BasicShapeType.Trapezoid,
    label: 'Trapezoid Up',
    iconDimensions: { ...ICON_LANDSCAPE_DIM },
    icon: <TrapezoidIcon />,
  },
  {
    key: 'vg-icon-block-arrow',
    type: BasicShapeType.BlockArrow,
    label: 'Block Arrow',
    iconDimensions: { ...ICON_LANDSCAPE_DIM },
    icon: <BlockArrowIcon />,
  },
  {
    key: 'vg-icon-block-arrow-indent',
    type: BasicShapeType.BlockArrowIndent,
    label: 'Block Arrow Indent',
    iconDimensions: { ...ICON_LANDSCAPE_DIM },
    icon: <BlockArrowIndentIcon />,
  },
  {
    key: 'vg-icon-ribbon',
    type: BasicShapeType.Ribbon,
    label: 'Ribbon',
    iconDimensions: { ...ICON_FLAT_LANDSCAPE_DIM },
    icon: <RibbonIcon />,
  },
  {
    key: 'vg-icon-double-block-arrow',
    type: BasicShapeType.DoubleBlockArrow,
    label: 'Double Block Arrow',
    iconDimensions: { ...ICON_FLAT_LANDSCAPE_DIM },
    icon: <DoubleBlockArrowIcon />,
  },
];
